/**
 * L'enchaînement des cinq étapes de la résiliation, et ce que chacune passe à la suivante.
 *
 * Port littéral de `src/student/account/subscription/cancel-flow/lib/flow.js:5-15` : les points
 * d'insertion restent des fonctions, pour qu'une étape puisse s'intercaler sans toucher les
 * écrans. Les noms de route sont ceux de `navigationScreens.js:1810-1839`, inchangés — les deux
 * chemins (legacy et ports) empruntent les mêmes.
 *
 * Les paramètres de route sont repris à l'identique : chaque étape rend `{...route.params}` et y
 * ajoute les siens, donc le motif de l'étape 1 arrive jusqu'à l'accusé de l'étape 5.
 */

export const CANCEL_ROUTE_REASON = "cancel-reason"
export const CANCEL_ROUTE_ALTERNATIVES = "cancel-alternatives"
export const CANCEL_ROUTE_FEES = "cancel-fees"
export const CANCEL_ROUTE_PAYMENT = "cancel-payment"
export const CANCEL_ROUTE_GOODBYE = "cancel-goodbye"

/** `getNextScreenAfterReason` */
export function nextScreenAfterReason(): string {
    return CANCEL_ROUTE_ALTERNATIVES
}

/** `getNextScreenAfterAlternatives` */
export function nextScreenAfterAlternatives(): string {
    return CANCEL_ROUTE_FEES
}

/** `getNextScreenAfterFees` */
export function nextScreenAfterFees(): string {
    return CANCEL_ROUTE_PAYMENT
}

/**
 * Ce que les paramètres de route portent d'une étape à l'autre. Tout est facultatif : le parcours
 * s'ouvre depuis le portail avec `{ studentId }` seul (`ParentSubscriptionPortalScreen.js:658-660`)
 * et se remplit en avançant.
 */
export type CancelFlowRouteParams = {
    studentId?: string | number
    motif?: string
    motif_free_text?: string | null
    // ── posés par l'étape 3
    fees_cents?: number
    credit_applied_cents?: number
    balance_due_cents?: number
    amount_due_cents?: number
    end_of_month?: string | null
    commitment_ends_at?: string | null
    remaining_months?: number | null
    default_pm?: unknown
    parent_email?: string | null
    milestone_cancellation?: boolean | string
    resume_date?: string | null
    // ── posés par l'étape 3 ou 4, avant l'accusé
    outcome?: string
    paid_cents?: number
}

/**
 * Ce que les cinq étapes utilisent de React Navigation. Chaque crochet le prend par
 * `useNavigation<CancelFlowNavigation>()`, comme partout ailleurs sous `ports/**` — la table de
 * navigation monte les écrans, elle ne leur passe que leur `route`.
 */
export type CancelFlowNavigation = {
    push: (name: string, params?: Record<string, unknown>) => void
    replace: (name: string, params?: Record<string, unknown>) => void
    navigate: (name: string, params?: Record<string, unknown>) => void
    goBack: () => void
    popToTop: () => void
}

export type CancelFlowScreenProps = {
    route?: { params?: CancelFlowRouteParams | null } | null
}

/**
 * Le retour au portail que les alternatives empruntent — `CancelAlternativesScreen.js:165-182`.
 * L'écran dépile le parcours puis rouvre l'onglet Abonnement en lui signalant quoi ouvrir ; le
 * portail écoute `openSupport` / `triggerPause` (`ParentSubscriptionPortalScreen.js:298-312`).
 */
export function backToSubscriptionPortal(
    navigation: CancelFlowNavigation,
    params: { openSupport?: boolean; triggerPause?: boolean; fromCancellation?: boolean }
): void {
    navigation.popToTop()
    navigation.navigate("menu", { screen: "parent-subscription", params })
}
